// KycRiskBadge.js — Reusable risk rating badge for KYC cases
// Maps risk ratings to coloured pills, optionally shows numeric score

import React from 'react';

const RISK_CONFIG = {
  low:       { label: 'Low',       bg: 'bg-green-100',  text: 'text-green-800',  dot: 'bg-green-500',  bar: 'bg-green-500'  },
  medium:    { label: 'Medium',    bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500', bar: 'bg-yellow-500' },
  high:      { label: 'High',      bg: 'bg-orange-100', text: 'text-orange-800', dot: 'bg-orange-500', bar: 'bg-orange-500' },
  very_high: { label: 'Very High', bg: 'bg-red-100',    text: 'text-red-800',    dot: 'bg-red-500',    bar: 'bg-red-500'    },
};

const SIZE_CLASSES = {
  xs: 'px-1.5 py-0.5 text-[10px]',
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
  lg: 'px-3 py-1.5 text-sm',
};

const KycRiskBadge = ({ rating, score, size = 'sm', showScore = false, className = '' }) => {
  const config = RISK_CONFIG[rating] || {
    label: rating || 'Unrated',
    bg: 'bg-gray-100',
    text: 'text-gray-600',
    dot: 'bg-gray-400',
    bar: 'bg-gray-400',
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 font-medium rounded-full ${config.bg} ${config.text} ${SIZE_CLASSES[size] || SIZE_CLASSES.sm} ${className}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot} flex-shrink-0`} />
      {config.label}
      {showScore && score !== undefined && score !== null && (
        <span className="opacity-70">({score})</span>
      )}
    </span>
  );
};

/**
 * Horizontal risk score bar for risk factor breakdowns.
 * Shows a coloured bar proportional to score (0-10).
 */
export const KycRiskBar = ({ label, score, maxScore = 10 }) => {
  const pct = Math.min(Math.max((score / maxScore) * 100, 0), 100);
  let barColour = 'bg-green-500';
  if (score >= 7.5) barColour = 'bg-red-500';
  else if (score >= 5) barColour = 'bg-orange-500';
  else if (score >= 2.5) barColour = 'bg-yellow-500';

  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-gray-600 w-32 flex-shrink-0">{label}</span>
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${barColour}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-sm font-medium text-gray-700 w-10 text-right">{score.toFixed(1)}</span>
    </div>
  );
};

export default KycRiskBadge;
